<script setup lang="ts">
import ToolbarComponent from '@/components/ToolbarComponent.vue';
import { useUserStore } from '@/stores/userStore';
import { BookSearch24Regular, BranchFork24Regular, BuildingFactory24Regular, BuildingMultiple24Regular, BuildingRetail24Regular, CameraDome24Regular, Certificate24Regular, Connector24Regular, ContactCard24Regular, ContentSettings24Regular, CreditCardToolbox24Regular, Directions24Regular, Flowchart24Regular, Gauge24Regular, Location24Regular, Payment24Regular, PeopleAudience24Regular, Person24Regular, ReadingList24Regular, Server24Regular, ShoppingBag24Regular, Tag24Regular, Toolbox24Regular } from '@vicons/fluent';
import { storeToRefs } from 'pinia';
import { type RouteLocationRaw } from 'vue-router';
import config from '@/services/ConfigService'
import { Permission } from '@/infrastructure/enums';

interface Setting {
  title: string
  description: string
  route: RouteLocationRaw
  icon: Component
  requiresSuperAdmin?: boolean
  requiredPermission?: Permission
  hide?: boolean
}

interface SettingGroup {
  title: string
  settings: Setting[],
  requiresSuperAdmin?: boolean
}

const { hasPermission } = useUserStore()
const { isSuperAdmin } = storeToRefs(useUserStore())

const groups = computed<SettingGroup[]>(() => {
  let items: SettingGroup[] = [
    {
      title: 'Your organisation',
      settings: [
        {
          title: 'Companies',
          description: 'The companies that own and operate one or more centers',
          route: { name: 'companies' },
          icon: BuildingMultiple24Regular,
          requiredPermission: Permission.CompaniesRead
        },
        {
          title: 'Centers',
          description: 'Configure the shopping centers available in the system',
          route: { name: 'centers' },
          icon: BuildingRetail24Regular,
          requiredPermission: Permission.CompaniesRead
        },
        {
          title: 'Locations',
          description: 'Manage the locations or areas of your center',
          route: { name: 'locations' },
          icon: Location24Regular,
          requiredPermission: Permission.LocationsRead
        },
        {
          title: 'Contacts',
          description: 'Used system-wide for different purposes',
          route: { name: 'contacts' },
          icon: ContactCard24Regular,
          requiredPermission: Permission.ContactsRead
        },
        {
          title: 'Status types',
          description: 'The flow new operators and units will go through',
          route: { name: 'status-types' },
          icon: Flowchart24Regular,
          requiredPermission: Permission.StatusTypesRead
        },
        {
          title: 'Shop categories',
          description: 'Manage the categories found in your shopping center',
          route: { name: 'shop-categories' },
          icon: ShoppingBag24Regular,
          requiresSuperAdmin: true
        },
        {
          title: 'Sales channels',
          description: 'Add or remove the sales channels shops can use',
          route: { name: 'sales-channels' },
          icon: Payment24Regular,
          requiresSuperAdmin: true
        },
        {
          title: 'Utility meters',
          description: 'Utility meters can be used to set up invoicing base fees',
          route: { name: 'utility-meters' },
          icon: Gauge24Regular,
          requiresSuperAdmin: true
        },
        {
          title: 'Brands',
          description: 'Saucony, Prada, Ray-Ban, etc. All the different brands that can be used as mapping output values',
          route: { name: 'product-brands' },
          icon: Tag24Regular,
          requiresSuperAdmin: true
        },
        {
          title: 'Passenger settings',
          description: 'Manage passenger locations and types',
          route: { name: 'pax-settings' },
          icon: PeopleAudience24Regular,
          requiredPermission: Permission.LocationsRead,
          hide: !config.features.invoicing
        },
      ]
    },
    {
      title: 'User management',
      settings: [
        {
          title: 'Users',
          description: 'Manage users or invite new ones',
          route: { name: 'users' },
          icon: Person24Regular,
          requiredPermission: Permission.UsersRead
        },
        {
          title: 'Permission sets',
          description: 'Create and manage custom permission sets',
          route: { name: 'permission-sets' },
          icon: Certificate24Regular,
          requiredPermission: Permission.PermissionSetsRead
        },
        {
          title: 'Audit log',
          description: 'Access activity history in one place',
          route: { name: 'audit-logs' },
          icon: BookSearch24Regular,
          requiresSuperAdmin: true
        }
      ]
    },
    {
      title: 'Integration',
      settings: [
        {
          title: 'Integrators',
          description: 'Set up the available integrators for operators',
          route: { name: 'integrators' },
          icon: Connector24Regular,
          requiredPermission: Permission.IntegratorsRead
        },
        {
          title: 'POS systems',
          description: 'Create and manage your Point of Sale systems',
          route: { name: 'pos-systems' },
          icon: CreditCardToolbox24Regular,
          requiredPermission: Permission.PosSystemsRead
        }
      ]
    },
    {
      title: 'Admin tools',
      requiresSuperAdmin: true,
      settings: [
        {
          title: 'Global settings',
          description: 'Deeper system configuration',
          route: { name: 'global-settings' },
          icon: ContentSettings24Regular
        },
        {
          title: 'Job queue',
          description: 'Monitor and manipulate jobs',
          route: { name: 'jobs' },
          icon: BuildingFactory24Regular
        },
        {
          title: 'Logs',
          description: 'Browse logs',
          route: { name: 'logs' },
          icon: CameraDome24Regular
        },
        {
          title: 'FTP logs',
          description: 'Browse FTP logs',
          route: { name: 'ftp-logs' },
          icon: ReadingList24Regular
        },
        {
          title: 'Tools',
          description: 'Toggle service mode, trigger jobs manually and run importers',
          route: { name: 'tools' },
          icon: Toolbox24Regular
        },
        {
          title: 'Push nodes',
          description: 'Manage Push HA nodes and their configurations',
          route: { name: 'push-nodes' },
          icon: Server24Regular
        },
        {
          title: 'Contract instructions',
          description: 'View instructions given to the contracts service',
          route: { name: 'instructions' },
          icon: Directions24Regular
        },
        {
          title: 'Manual mapping',
          description: 'Create one-to-one mappings based on existing data',
          route: { name: 'mapping-manual' },
          icon: BranchFork24Regular
        },
      ]
    }
  ]

  // If user is super admin, don't filter anything
  if (isSuperAdmin.value) return items

  else {
    items = items
      .filter(group => !group.requiresSuperAdmin)
      .map(group => ({
        ...group,
        settings: group.settings
          .filter(setting => !setting.hide)
          .filter(setting => !setting.requiresSuperAdmin && (!setting.requiredPermission || hasPermission(setting.requiredPermission)))
      }))

    // If there are empty groups after filtering settings, remove them
    items = items.filter(group => group.settings.length)

    return items
  }
})
</script>

<template>
  <ToolbarComponent title="Settings">
    <template #extra>
      <p class="text-neutral-400 text-xs">
        Version {{ config.version }} - build {{ config.build }}
      </p>
    </template>
  </ToolbarComponent>

  <section
    v-for="group in groups"
    :key="group.title"
    class="mb-10 max-w-[1400px] mx-auto"
  >
    <h3 class="text-base font-bold">
      {{ group.title }}
    </h3>
    <n-grid cols="1 600:2 900:3 1200:4" :x-gap="24" :y-gap="24" class="mt-3">
      <n-gi v-for="setting in group.settings" :key="setting.title" class="hover:bg-neutral-700 dark:hover:neutral-700">
        <RouterLink :to="setting.route">
          <n-card
            size="small"
            class="hover:bg-neutral-100! dark:hover:bg-zinc-800! h-full"
          >
            <header class="text-base font-bold flex gap-2 items-center">
              <n-icon :component="setting.icon" :size="20" />
              <span>
                {{ setting.title }}
              </span>
            </header>
            <p class="opacity-60 mt-3 text-sm">
              {{ setting.description }}
            </p>
          </n-card>
        </RouterLink>
      </n-gi>
    </n-grid>
  </section>
</template>